export function initImgZoom($this: HTMLElement): void {
	//Img Zoom Elements
	const ZoomInBtn = $this.querySelector('.gb-zoom-in') as HTMLElement
	const ZoomOutBtn = $this.querySelector('.gb-zoom-out') as HTMLElement
	const fullscreenBtn = $this.querySelector('.gb-zoom-fullscreen')
	const wrapper = $this.querySelector('.gb-primary-wrapper') as HTMLElement
	const imgContainer = $this.querySelector('.gb-wrapper-img-container') as HTMLElement
	const universalImgContainer = $this.querySelector('.universal-image-wrapper-container') as HTMLElement
	//const img = $this.querySelector('.gb-universal-image') as HTMLElement
	const btnCrlContainer = $this.querySelector('.gb-zoom-btn-container') as HTMLElement
	const img = universalImgContainer.querySelector('img')

	//Configurations
	const maxZoom = 4
	const minZoom = 1
	const maxDelay: number = 300
	const observer = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					btnCrlContainer.style.zIndex = '5'
				} else {
					btnCrlContainer.style.zIndex = '1'
				}
			})
		},
		{ threshold: 0.1 }
	)
	observer.observe(wrapper)
	const zoomIncrement: number = 0.25
	let offsetX = 0,
		offsetY = 0
	let currentXPos = 0,
		currentYPos = 0
	let currentZoom = minZoom
	let lastTapTime = 0
	//let tapTimeout: number
	let imgMoving = false
	let isMultiTouch = false
	let initialDist: number
	let initialScale = 1
	let fullscreenMode = false
	let timeout
	let isZooming = false
	let isFullscreenToggling = false
	let isDragging = false

	const fullScreenToggle = (): void => {
		if (isFullscreenToggling) return
		isFullscreenToggling = true

		if (wrapper.requestFullscreen && !fullscreenMode) {
			wrapper.requestFullscreen().finally(() => {
				isFullscreenToggling = false
			})
		} else if (fullscreenMode) {
			document.exitFullscreen().finally(() => {
				isFullscreenToggling = false
			})
		}
		isFullscreenToggling = false
	}

	const zoomIn = (): number => {
		if (isZooming) return currentZoom
		isZooming = true

		currentZoom = isMaxZoomed() ? currentZoom : (currentZoom += zoomIncrement)

		if (!isMinZoomed()) {
			ZoomOutBtn.classList.remove('gb-disabled')
		}

		if (isMaxZoomed()) {
			ZoomInBtn.classList.add('gb-disabled')
		} else {
			ZoomInBtn.classList.remove('gb-disabled')
		}

		// Add zoom class to enable horizontal panning
		if (currentZoom > minZoom) {
			universalImgContainer.querySelector('.gb-universal-image')?.classList.add('gb-zoomed-overflow-enabled')
		}

		isZooming = false
		return currentZoom
	}

	const zoomOut = (): number => {
		currentZoom = isMinZoomed() ? currentZoom : (currentZoom -= zoomIncrement)

		if (isMinZoomed()) {
			ZoomOutBtn.classList.add('gb-disabled')
			universalImgContainer.querySelector('.gb-universal-image')?.classList.remove('gb-zoomed-overflow-enabled')
		} else {
			ZoomOutBtn.classList.remove('gb-disabled')
		}

		if (!isMaxZoomed()) {
			ZoomInBtn.classList.remove('gb-disabled')
		}

		return currentZoom
	}

	//Event Listeners
	imgContainer.addEventListener('mousedown', startDrag)
	imgContainer.addEventListener('mouseup', stopDrag)
	ZoomInBtn.addEventListener('click', ZoomInImg)
	ZoomOutBtn.addEventListener('click', zoomOutImg)
	fullscreenBtn.addEventListener('click', fullScreenToggle)
	imgContainer.addEventListener('touchstart', touchStartImg)
	imgContainer.addEventListener('touchmove', touchMoveImg)
	imgContainer.addEventListener('touchend', touchEndImg)
	document.addEventListener('fullscreenchange', onFullScreenChange)

	//Functions
	function getImgBoundary() {
		const uniImgRect = img.getBoundingClientRect()
		const wrapRect = imgContainer.getBoundingClientRect()

		// Get current scaled dimensions
		const scaledImgHeight = uniImgRect.height
		const scaledImgWidth = uniImgRect.width

		let maxDistTop = 0
		let maxDistLeft = 0

		// Vertical bounds
		if (scaledImgHeight <= wrapRect.height) {
			maxDistTop = (wrapRect.height - scaledImgHeight) / 2 / currentZoom
		} else {
			maxDistTop = (scaledImgHeight - wrapRect.height) / 2 / currentZoom
		}

		// Horizontal bounds
		if (scaledImgWidth <= wrapRect.width) {
			maxDistLeft = (wrapRect.width - scaledImgWidth) / 2 / currentZoom
		} else {
			maxDistLeft = (scaledImgWidth - wrapRect.width) / 2 / currentZoom
		}

		return { maxDistX: maxDistLeft, maxDistY: maxDistTop }
	}

	function calcImgBoundaryPos(coorXY, maxDist) {
		if (Math.abs(coorXY) > maxDist) {
			coorXY = coorXY < 0 ? -maxDist : maxDist
		}

		return coorXY
	}

	function resetImg() {
		//re centers image to original state
		currentZoom = minZoom
		currentXPos = 0
		currentYPos = 0

		universalImgContainer.style.transform = `scale(${minZoom}) translate(${currentXPos}px, ${currentYPos}px)`

		img.style.width = ''
		imgContainer.style.height = ''
		imgContainer.classList.remove('gb-zoomed-in')
		universalImgContainer.querySelector('.gb-universal-image')?.classList.remove('gb-zoomed-overflow-enabled')
		ZoomInBtn.classList.remove('gb-disabled')
		btnCrlContainer.classList.remove('gb-hide')
	}

	function onFullScreenChange() {
		fullscreenMode = fullscreenMode ? false : true
	}

	function touchEndImg(e) {
		imgMoving = false

		// If transitioning from pinch-to-zoom to single-finger drag,
		// recalculate offset from the remaining touch to prevent the image from jumping
		if (isMultiTouch && e.touches.length === 1) {
			offsetX = e.touches[0].pageX - currentXPos
			offsetY = e.touches[0].pageY - currentYPos
		}

		isMultiTouch = e.touches.length >= 2
	}

	function touchMoveImg(e) {
		if (isMultiTouch) {
			e.preventDefault()
		} else {
			preventDefaultOnZoom(e)
		}

		if (currentZoom === 1 && !isMultiTouch) {
			imgMoving = false

			return
		}

		if (isMultiTouch) {
			const touch1 = e.touches[0]
			const touch2 = e.touches[1]

			const currentDist = getDistance(touch1, touch2)
			const pinchOpen = currentDist > initialDist

			const scale = (currentDist / initialDist) * initialScale

			if (isMaxZoomed() && pinchOpen) return

			if (scale <= maxZoom && scale > minZoom) {
				currentZoom = scale
				universalImgContainer.style.transform = `scale(${currentZoom}) translate(${currentXPos}px, ${currentYPos}px)`
			} else if (scale <= minZoom) {
				resetImg()
			}

			return
		}

		imgMoving = true
		;[...e.changedTouches].forEach(touch => {
			//Gets the Most Px you can move on either axis.
			const maxDistLeft = getImgBoundary().maxDistX
			const maxDistTop = getImgBoundary().maxDistY

			const x = calcImgBoundaryPos(touch.pageX - offsetX, maxDistLeft)
			const y = calcImgBoundaryPos(touch.pageY - offsetY, maxDistTop)

			currentXPos = x
			currentYPos = y

			universalImgContainer.style.transform = `scale(${currentZoom}) translate(${x}px, ${y}px)`
		})
	}

	function touchStartImg(e) {
		if (e.touches.length >= 2) {
			e.preventDefault()
		} else {
			preventDefaultOnZoom(e)
		}

		isMultiTouch = e.touches.length >= 2

		if (isMultiTouch) {
			//Pinch/Zoom Event
			isMultiTouch = true
			const touch1 = e.touches[0]
			const touch2 = e.touches[1]

			initialDist = getDistance(touch1, touch2)
			initialScale = currentZoom

			return
		}

		if (!isMultiTouch) {
			const currentTime = new Date().getTime()
			const tapTime = currentTime - lastTapTime

			if (tapTime < maxDelay && tapTime > 0) {
				clearTimeout(timeout)
				if (isMinZoomed()) {
					// Calculate scale needed for height: 100% WITHOUT actually applying it
					const originalHeight = img.offsetHeight
					const currentContainerHeight = imgContainer.offsetHeight

					// Get what 100% height would be (parent container height)
					const parentHeight = imgContainer.parentElement.offsetHeight

					// Calculate how much the container would grow
					const containerGrowthRatio = parentHeight / currentContainerHeight

					// Calculate what the image height would become
					const theoreticalNewHeight = originalHeight * containerGrowthRatio

					// Calculate the scale this represents
					const calculatedScale = theoreticalNewHeight / originalHeight
					currentZoom = Math.min(calculatedScale, maxZoom) // Don't exceed max zoom

					// Apply the transform to match the visual scale
					universalImgContainer.style.transform = `scale(${currentZoom}) translate(${currentXPos}px, ${currentYPos}px)`
				} else {
					resetImg()
				}
			} else {
				timeout = setTimeout(() => {
					if (imgMoving) return
					toggleImgCtrls()
				}, 400)
			}

			lastTapTime = currentTime
		}

		if (currentZoom === 1) return

		offsetX = e.targetTouches[0].pageX - currentXPos
		offsetY = e.targetTouches[0].pageY - currentYPos
	}

	function zoomOutImg(e) {
		preventDefaultOnZoom(e)

		const currentZoomOut = zoomOut()

		//scale first to get new boundaries + offset
		universalImgContainer.style.transform = `scale(${currentZoom})`

		offsetX = e.clientX - currentXPos
		offsetY = e.clientY - currentYPos

		const maxDistLeft = getImgBoundary().maxDistX
		const maxDistTop = getImgBoundary().maxDistY

		const x = calcImgBoundaryPos(e.clientX - offsetX, maxDistLeft)
		const y = calcImgBoundaryPos(e.clientY - offsetY, maxDistTop)

		currentXPos = x
		currentYPos = y

		if (currentZoomOut <= 1 || isMinZoomed()) {
			resetImg()
		}

		universalImgContainer.style.transform = `scale(${currentZoom}) translate(${currentXPos}px, ${currentYPos}px)`
	}

	function ZoomInImg(e) {
		preventDefaultOnZoom(e)

		const currentZoomIn = zoomIn()
		imgContainer.classList.add('gb-zoomed-in')

		if (currentZoomIn > maxZoom) {
			currentZoom = maxZoom
		}

		// Constrain position to new boundaries after zoom
		const maxDistLeft = getImgBoundary().maxDistX
		const maxDistTop = getImgBoundary().maxDistY

		currentXPos = calcImgBoundaryPos(currentXPos, maxDistLeft)
		currentYPos = calcImgBoundaryPos(currentYPos, maxDistTop)

		universalImgContainer.style.transform = `scale(${currentZoom}) translate(${currentXPos}px, ${currentYPos}px)`
	}

	function getDistance(touch1: Touch, touch2: Touch): number {
		const dx = touch1.clientX - touch2.clientX
		const dy = touch1.clientY - touch2.clientY
		return Math.sqrt(dx * dx + dy * dy)
	}

	function toggleImgCtrls(): void {
		btnCrlContainer.classList.toggle('gb-hide')
	}

	function startDrag(e) {
		preventDefaultOnZoom(e)
		if (e.button !== 0 || isMinZoomed()) return
		isDragging = true

		offsetX = e.clientX - currentXPos
		offsetY = e.clientY - currentYPos

		imgContainer.addEventListener('mousemove', dragImage)
	}

	function stopDrag() {
		if (!isDragging) return
		isDragging = false

		imgContainer.removeEventListener('mousemove', dragImage)
	}

	function dragImage(e) {
		//Gets the Most Px you can move on either axis.
		const maxDistLeft = getImgBoundary().maxDistX
		const maxDistTop = getImgBoundary().maxDistY

		const x = calcImgBoundaryPos(e.clientX - offsetX, maxDistLeft)
		const y = calcImgBoundaryPos(e.clientY - offsetY, maxDistTop)

		currentXPos = x
		currentYPos = y

		universalImgContainer.style.transform = `scale(${currentZoom}) translate(${x}px, ${y}px)`
	}

	function isMinZoomed() {
		return currentZoom <= minZoom
	}

	function isMaxZoomed() {
		return currentZoom >= maxZoom
	}

	function preventDefaultOnZoom(e) {
		if (!isMinZoomed()) {
			e.preventDefault()
		}
	}
}
